import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import config from "../configs/config";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import CustomizedVariable from "./CustomizedVariable";
import "./styles/Weighting.css";
import Loader from "./Loader";

function Weighting() {
  const navigate = useNavigate();
  const {
    dataGroup,
    projectId,
    savedWeightings,
    saveCurrentWeighting,
    currentSelectedVariablesset,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    weightingData,
    setWeightingData,
    weightingStats,
    setWeightingStats,
    currentSelectedVariables,
    setCurrentSelectedVariables,
    selectedProject,
    selectedClient,
    powner,
  } = useContext(VariableContext);

  const { handleTitleClick, setActivePage, loading, setLoading } =
    useContext(FetchingFreq);
  const { toast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [percentageWarnings, setPercentageWarnings] = useState({});
  const [filterExpression, setFilterExpression] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);

  // ─── LTV Weighting state ─────────────────────────────────────────────────────
  const [showLtvPanel, setShowLtvPanel] = useState(false);
  const [ltvSelectedKeys, setLtvSelectedKeys] = useState([]);
  const [ltvResults, setLtvResults] = useState([]);
  const [ltvLoading, setLtvLoading] = useState(false);

  useEffect(() => {
    setActivePage("Weighting");
  }, []);

  // ─── Derived totals per variable ────────────────────────────────────────────
  const getTotalFreq = (variable) =>
    Object.values(weightingData[variable] || {}).reduce(
      (sum, row) => sum + (row.freq || 0),
      0
    );

  const getTotalWeightedCount = (variable) => {
    const total = Object.values(weightingData[variable] || {}).reduce(
      (sum, row) => sum + (row.weightedCount != null ? row.weightedCount : (row.freq || 0)),
      0
    );
    return total || getTotalFreq(variable);
  };

  // ─── Adjusted target calculation ────────────────────────────────────────────
  // Returns 0-1 proportions for all keys. For valid keys with targets, the target
  // is re-proportioned so that missing/NaN respondents keep their actual share and
  // the specified target % applies only among valid (non-missing) respondents.
  const calculateAdjustedTargets = (variable) => {
    const varData = weightingData[variable] || {};
    const totalFreq = getTotalFreq(variable);
    if (totalFreq === 0) return {};

    const keysWithTargets = Object.keys(varData).filter(
      (k) => varData[k].target !== '' && varData[k].target != null
    );
    const keysWithoutTargets = Object.keys(varData).filter(
      (k) => !keysWithTargets.includes(k)
    );

    // Sum of freq for valid (non-NaN, numeric) keys that have a target set
    const validFreqWithTarget = Object.entries(varData)
      .filter(([key]) => key !== 'NaN' && !isNaN(Number(key)) && keysWithTargets.includes(key))
      .reduce((sum, [, row]) => sum + (row.freq || 0), 0);

    const result = {};
    Object.keys(varData).forEach((key) => {
      const isValidKey = key !== 'NaN' && !isNaN(Number(key));
      if (isValidKey && keysWithTargets.includes(key)) {
        const target = parseFloat(varData[key].target) || 0;
        // target is a % among valid respondents → scale down by (validFreq / totalFreq)
        result[key] = (target / 100) * (validFreqWithTarget / totalFreq);
      } else {
        // Missing/NaN keys or keys without targets keep their actual proportion
        result[key] = (varData[key].freq || 0) / totalFreq;
      }
    });
    return result;
  };

  // ─── Validation ─────────────────────────────────────────────────────────────
  const validatePercentages = () => {
    const warnings = {};
    Object.entries(weightingData).forEach(([variable, keys]) => {
      const targets = Object.values(keys).map((r) => parseFloat(r.target) || 0);
      if (targets.some((t) => t > 0)) {
        const total = targets.reduce((s, t) => s + t, 0);
        if (Math.abs(total - 100) >= 0.01) {
          warnings[variable] = { total, isValid: false };
        }
      }
    });
    setPercentageWarnings(warnings);
    return warnings;
  };

  useEffect(() => {
    if (Object.keys(weightingData).length > 0) validatePercentages();
  }, [weightingData]);

  // ─── Save to backend ─────────────────────────────────────────────────────────
  const saveCurrentWeightingBackend = async () => {
    setLoading(true);
    try {
      await apiService.post(`/saveweightings/${projectId}`, { project_id: projectId, savedWeightings });
      toast.success("Weightings saved successfully!");
    } catch (error) {
      console.error("Error saving weightings:", error);
      toast.error("Error occurred while saving weightings.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Load a saved weighting ──────────────────────────────────────────────────
  const loadWeighting = (weightingName) => {
    const selected = savedWeightings[weightingName];
    if (!selected) return;

    let data = selected.data;
    // Backward-compat with old format
    if (!data && selected.targetPercentages) {
      const tp = selected.targetPercentages || {};
      const wc = selected.weightedCounts || {};
      const fd = selected.frequencyData || {};
      data = {};
      Object.entries(tp).forEach(([variable, keys]) => {
        data[variable] = {};
        Object.entries(keys).forEach(([key, target]) => {
          data[variable][key] = {
            freq: fd[variable]?.[key] ?? 0,
            target: target ?? '',
            weightedCount: wc[variable]?.[key] ?? null,
          };
        });
      });
    }

    const variables = Object.keys(data || {});
    setCurrentSelectedVariables(variables);
    setcurrentSelectedVariablesset(variables);
    setWeightingData({ ...(data || {}) });
    setWeightingStats({ ...selected.stats });
  };

  // ─── Fetch frequencies ───────────────────────────────────────────────────────
  const fetchFrequencyData = async () => {
    if (!currentSelectedVariablesset.length) {
      toast.warning("No variables selected. Please select variables from the sidebar first.");
      return;
    }
    setLoading(true);
    try {
      const response = await apiService.post("/wtfrequency", {
        variables: currentSelectedVariablesset,
        projectId,
        filter: isFilterActive ? filterExpression : null,
        user: powner,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      mergeFrequencyResponse(response.data);
      toast.success(`Frequency data loaded for ${currentSelectedVariablesset.length} variable(s).`);
    } catch (error) {
      console.error("Error fetching frequency data:", error);
      toast.error("Failed to fetch frequency data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFrequencyDataForVariables = async (variables) => {
    setLoading(true);
    try {
      const response = await apiService.post("/wtfrequency", {
        currentSelectedVariablesset: variables,
        projectId,
        filter: isFilterActive ? filterExpression : null,
        user: powner,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      mergeFrequencyResponse(response.data);
    } catch (error) {
      console.error("Error fetching frequency data:", error);
      toast.error("Failed to fetch frequency data.");
    } finally {
      setLoading(false);
    }
  };

  // Merge backend freq response into weightingData (preserves existing targets)
  const mergeFrequencyResponse = (raw) => {
    setWeightingData((prev) => {
      const updated = { ...prev };
      Object.entries(raw).forEach(([variable, keys]) => {
        if (!updated[variable]) updated[variable] = {};
        Object.entries(keys).forEach(([key, rowData]) => {
          const freqVal = typeof rowData === 'object' ? (rowData.freq ?? 0) : (rowData ?? 0);
          updated[variable][key] = {
            freq: freqVal,
            label: typeof rowData === 'object' ? (rowData.label ?? '') : '',
            target: updated[variable]?.[key]?.target ?? '',
            weightedCount: updated[variable]?.[key]?.weightedCount ?? null,
          };
        });
      });
      return updated;
    });
  };

  // ─── Target change ───────────────────────────────────────────────────────────
  const handleTargetChange = (variable, key, value) => {
    setWeightingData((prev) => ({
      ...prev,
      [variable]: {
        ...prev[variable],
        [key]: { ...prev[variable][key], target: value },
      },
    }));
  };

  // ─── Paste handler ───────────────────────────────────────────────────────────
  const getAllVariableKeys = () => {
    const all = [];
    Object.entries(weightingData).forEach(([variable, keys]) => {
      Object.keys(keys).forEach((key) => all.push({ variable, key }));
    });
    return all;
  };

  const handlePaste = (e, startVariable, startKey) => {
    e.preventDefault();
    const entries = e.clipboardData
      .getData("text")
      .split(/\r\n|\r|\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!entries.length) return;

    const allKeys = getAllVariableKeys();
    const startIndex = allKeys.findIndex(
      (vk) => vk.variable === startVariable && vk.key === startKey
    );
    if (startIndex === -1) return;

    // Map each pasted line to its {variable, key, parsed} triple
    const assignments = [];
    entries.forEach((value, i) => {
      const targetIndex = startIndex + i;
      if (targetIndex >= allKeys.length) return;
      const { variable, key } = allKeys[targetIndex];
      // Strip % sign and any non-numeric chars (handles "60%", "2,500", etc.)
      const cleaned = value.replace(/%/g, "").replace(/[^0-9.]/g, "");
      const parsed = parseFloat(cleaned);
      assignments.push({ variable, key, parsed });
    });

    // Group by variable so we can normalize per-variable if needed
    const byVariable = {};
    assignments.forEach(({ variable, key, parsed }) => {
      if (!byVariable[variable]) byVariable[variable] = [];
      byVariable[variable].push({ key, parsed });
    });

    setWeightingData((prev) => {
      const updated = { ...prev };
      Object.entries(byVariable).forEach(([variable, items]) => {
        const validItems = items.filter(({ parsed }) => !isNaN(parsed));
        if (!validItems.length) return;
        const sum = validItems.reduce((s, { parsed }) => s + parsed, 0);
        // If pasted values don't sum to ~100, treat as counts and convert to %
        const shouldNormalize = sum > 0 && Math.abs(sum - 100) > 0.5;
        validItems.forEach(({ key, parsed }) => {
          const finalValue = shouldNormalize ? (parsed / sum) * 100 : parsed;
          if (!updated[variable]) return;
          updated[variable] = {
            ...updated[variable],
            [key]: {
              ...updated[variable][key],
              target: isNaN(finalValue) ? '' : parseFloat(finalValue.toFixed(4)),
            },
          };
        });
      });
      return updated;
    });
  };

  // ─── Apply weighting ─────────────────────────────────────────────────────────
  const applyWeighting = async () => {
    const adjustedTargetDistributions = {};
    Object.keys(weightingData).forEach((variable) => {
      adjustedTargetDistributions[variable] = calculateAdjustedTargets(variable);
    });

    const weightVariableName = `weight${
      (savedWeightings && Object.keys(savedWeightings).length) || ""
    }`;

    setLoading(true);
    try {
      const response = await apiService.post("/applyweights", {
        target_distributions: adjustedTargetDistributions,
        weightingData,
        weight_variable_name: weightVariableName,
        projectId,
        user: powner,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
        filter: isFilterActive ? filterExpression : null,
      });

      const stats = {
        min: response.data.weight_min,
        max: response.data.weight_max,
        efficiency: response.data.cv,
        newefficiency: response.data.check,
        iterations: response.data.iteration,
        weightingName: weightVariableName,
      };
      setWeightingStats(stats);

      // Merge weightedCount back into weightingData
      const updatedData = response.data.weightingData || null;
      if (updatedData) {
        setWeightingData(updatedData);
        saveCurrentWeighting(weightVariableName, stats, updatedData);
      } else {
        // Fallback: merge from legacy weighted_counts
        const wc = response.data.weighted_counts || {};
        setWeightingData((prev) => {
          const merged = { ...prev };
          Object.entries(wc).forEach(([variable, keys]) => {
            if (!merged[variable]) return;
            Object.entries(keys).forEach(([key, count]) => {
              const k = String(key);
              if (merged[variable][k]) {
                merged[variable][k] = { ...merged[variable][k], weightedCount: count };
              }
            });
          });
          return merged;
        });
        saveCurrentWeighting(weightVariableName, stats, weightingData);
      }

      toast.success("Weighting applied successfully!");
      if (isFilterActive) {
        toast.info("Weights applied to filtered respondents only.");
      }
    } catch (error) {
      console.error("Error applying weights:", error);
      toast.error("Failed to apply weighting. Please check your targets and try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Clear / remove ──────────────────────────────────────────────────────────
  const clearTargets = () => {
    setWeightingData((prev) => {
      const cleared = {};
      Object.entries(prev).forEach(([variable, keys]) => {
        cleared[variable] = {};
        Object.entries(keys).forEach(([key, row]) => {
          cleared[variable][key] = { ...row, target: '' };
        });
      });
      return cleared;
    });
  };

  const removeVariable = (variable) => {
    setcurrentSelectedVariablesset((prev) => prev.filter((v) => v !== variable));
    setWeightingData((prev) => {
      const next = { ...prev };
      delete next[variable];
      return next;
    });
  };

  const clearvars = () => {
    setcurrentSelectedVariablesset([]);
    setWeightingData({});
  };

  const handleVariableSelectionChange = (event) => {
    const selectedOptions = Array.from(event.target.selectedOptions, (o) => o.value);
    const selectedOptionsset = Array.from(
      event.target.selectedOptions,
      (o) => dataGroup[o.value].varName.map((item) => item.Name)
    ).flat();
    setcurrentSelectedVariablesset(selectedOptionsset);
    setCurrentSelectedVariables(selectedOptions);
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const token = apiService.getToken();
      const response = await fetch(`${config.API_BASE_URL}/download_weighted_file`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "weighted.sav");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Weighted file downloaded.");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download weighted file.");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => setIsModalOpen((prev) => !prev);
  const closeModalAndUpdateButtons = () => setIsModalOpen(false);

  const handleSaveCustomVariable = async (customVariable) => {
    try {
      if (!customVariable?.name) throw new Error("Custom variable name is missing");
      setcurrentSelectedVariablesset((prev) => [...prev, customVariable.name]);
      try {
        const { data: result } = await apiService.post("/compute-newvar", { projectId, customVariable });
        if (result.success) toast.success(result.message || "Custom variable created.");
      } catch (err) {
        console.error("Error creating custom variable:", err);
      }
    } catch (error) {
      console.error("Error creating custom variable:", error);
      toast.error(error.message || "Error creating custom variable.");
    }
  };

  const hasTargets = Object.values(weightingData).some((keys) =>
    Object.values(keys).some((r) => r.target !== '' && r.target != null)
  );

  // ─── Label lookup: SPSS var name → valueLabels dict (from dataGroup) ─────────
  const varValueLabels = useMemo(() => {
    const map = {};
    currentSelectedVariables.forEach((qk) => {
      const qg = dataGroup[qk];
      if (!qg) return;
      const vl = qg.valueLabels || {};
      (qg.varName || []).forEach((v) => { map[v.Name] = vl; });
    });
    return map;
  }, [dataGroup, currentSelectedVariables]);

  const getLabel = (variable, key) => {
    // Prefer label stored in weightingData (comes from SPSS meta via backend)
    const stored = weightingData[variable]?.[key]?.label;
    if (stored) return stored;
    // Fall back to dataGroup.valueLabels
    const vl = varValueLabels[variable] || {};
    if (vl[key] !== undefined) return vl[key];
    // Try numeric variants: "1" ↔ "1.0" ↔ 1
    const num = parseFloat(key);
    if (!isNaN(num)) {
      if (vl[num] !== undefined) return vl[num];
      const alt = Number.isInteger(num) ? `${num}.0` : String(Math.round(num));
      if (vl[alt] !== undefined) return vl[alt];
    }
    return '';
  };

  // ─── Key sort: numeric ascending, NaN / non-numeric last ─────────────────────
  const sortKeys = (entries) =>
    [...entries].sort(([a], [b]) => {
      const na = parseFloat(a), nb = parseFloat(b);
      const aNum = !isNaN(na), bNum = !isNaN(nb);
      if (aNum && bNum) return na - nb;
      if (aNum) return -1;
      if (bNum) return 1;
      return String(a).localeCompare(String(b));
    });

  const thStyle = { padding: "6px 10px", textAlign: "left", border: "1px solid #c4b5fd" };
  const tdStyle = { padding: "5px 10px", border: "1px solid #e9d5ff" };

  // ─── LTV Weighting ───────────────────────────────────────────────────────────
  const applyLtvWeighting = async () => {
    if (!ltvSelectedKeys.length) return;
    setLtvLoading(true);
    setLtvResults([]);
    const varNames = ltvSelectedKeys.flatMap(
      (k) => (dataGroup[k]?.varName || []).map((v) => v.Name)
    );
    const weightVarName = weightingStats.weightingName;
    try {
      const res = await apiService.post("/ltv-weight", {
        projectId,
        ltvVariables: varNames,
        weightVariableName: weightVarName,
        user: powner,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      setLtvResults(res.data.results || []);
    } catch (err) {
      console.error("LTV weight error:", err);
      setLtvResults([{ variable: 'Error', status: 'error', reason: err.message }]);
    }
    setLtvLoading(false);
  };

  return (
    <div className="mainw-content">
      {Object.keys(weightingStats).length > 0 && (
        <div className="stats-weightings-container">
          <div className="weighting-stats">
            <div>
              <h3>Weighting Statistics - {weightingStats.weightVariableName}</h3>
              <p>Minimum Weight: {weightingStats.min}</p>
              <p>Maximum Weight: {weightingStats.max}</p>
              <p>Efficiency (CV): {weightingStats.efficiency}%</p>
              <p>New Efficiency (CV): {weightingStats.newefficiency}%</p>
              <p>Iterations: {weightingStats.iterations}</p>
            </div>
          </div>
          <div className="saved-weightings">
            <h2>Saved Weightings</h2>
            <select onChange={(e) => loadWeighting(e.target.value)} defaultValue="">
              <option value="" disabled>Select Saved Weighting</option>
              {Object.keys(savedWeightings).map((name, index) => (
                <option key={index} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="filter-section" style={{
        margin: "20px", padding: "15px", border: "1px solid #ddd",
        borderRadius: "5px", backgroundColor: "#f9f9f9"
      }}>
        <h3>Filter Respondents for Weighting</h3>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <textarea
              value={filterExpression}
              onChange={(e) => setFilterExpression(e.target.value)}
              placeholder="Enter filter expression (e.g., age > 30 and gender == 'Male')&#10;Leave empty to weight all respondents"
              rows={2}
              style={{
                width: "100%", padding: "8px", fontSize: "14px",
                fontFamily: "monospace", border: "1px solid #ccc", borderRadius: "4px"
              }}
            />
            <div style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}>
              <strong>Examples:</strong> age &gt; 30 | gender == 'Male' | income &gt;= 50000
            </div>
          </div>
          <div>
            <button
              onClick={() => {
                setIsFilterActive(!!filterExpression.trim());
                if (filterExpression.trim()) {
                  fetchFrequencyData();
                  toast.success(`Filter activated: ${filterExpression}`);
                } else {
                  setIsFilterActive(false);
                  fetchFrequencyData();
                  toast.info("Filter cleared. Weights will be calculated for all respondents.");
                }
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: filterExpression.trim() ? "#4CAF50" : "#ccc",
                color: "white", border: "none", borderRadius: "4px",
                cursor: filterExpression.trim() ? "pointer" : "not-allowed"
              }}
              disabled={!filterExpression.trim()}
            >
              Apply Filter
            </button>
            {isFilterActive && (
              <button
                onClick={() => { setFilterExpression(""); setIsFilterActive(false); fetchFrequencyData(); }}
                style={{
                  marginLeft: "10px", padding: "8px 16px", backgroundColor: "#f44336",
                  color: "white", border: "none", borderRadius: "4px", cursor: "pointer"
                }}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        {isFilterActive && (
          <div style={{
            marginTop: "10px", padding: "8px", backgroundColor: "#e8f5e9",
            borderRadius: "4px", color: "#2e7d32"
          }}>
            ✓ Filter active: "{filterExpression}"
          </div>
        )}
      </div>

      {/* ── Selected variables from sidebar ─────────────────────────── */}
      <div style={{
        margin: "0 20px 4px",
        padding: "10px 14px",
        background: currentSelectedVariables.length ? "#f5f3ff" : "#f9fafb",
        border: `1px solid ${currentSelectedVariables.length ? "#c4b5fd" : "#e5e7eb"}`,
        borderRadius: "6px",
      }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#5b21b6", marginBottom: "6px" }}>
          Selected Variables from Sidebar
          {currentSelectedVariables.length > 0 && (
            <span style={{
              marginLeft: "8px", background: "#7c3aed", color: "#fff",
              borderRadius: "10px", padding: "1px 7px", fontSize: "11px"
            }}>
              {currentSelectedVariables.length}
            </span>
          )}
        </div>
        {currentSelectedVariables.length === 0 ? (
          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
            No variables selected — click items in the left sidebar to add them here.
          </span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {currentSelectedVariables.map((v) => (
              <span key={v} style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                background: "#ede9fe", color: "#4c1d95", border: "1px solid #c4b5fd",
                borderRadius: "4px", padding: "2px 8px", fontSize: "12px", fontWeight: 500,
              }}>
                {v}
                <button
                  onClick={() => {
                    setCurrentSelectedVariables((prev) => prev.filter((x) => x !== v));
                    setcurrentSelectedVariablesset((prev) =>
                      prev.filter((s) => !(dataGroup[v]?.varName || []).map((n) => n.Name).includes(s))
                    );
                  }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#7c3aed", fontWeight: 700, padding: "0 0 0 2px",
                    fontSize: "13px", lineHeight: 1,
                  }}
                  title={`Remove ${v}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="frequency-data">
          <button onClick={fetchFrequencyData} disabled={loading}>Get Frequency Data</button>
          <button onClick={toggleModal} title="Update Properties" disabled={loading}>Add Variable</button>
          <CustomizedVariable
            isOpen={isModalOpen}
            onClose={closeModalAndUpdateButtons}
            setcurrentSelectedVariablesset={setcurrentSelectedVariablesset}
            dataGroup={dataGroup}
            projectId={projectId}
          />
          <button onClick={clearTargets} disabled={loading}>Clear Target Percentages</button>
          <button onClick={clearvars} disabled={loading}>Clear Variable List</button>
          {Object.keys(weightingStats).length > 0 && (
            <>
              <button onClick={handleDownload} disabled={loading}>Download Weighted File</button>
              <button onClick={saveCurrentWeightingBackend} disabled={loading}>Save Current Weighting</button>
            </>
          )}
          {hasTargets && (
            <button onClick={applyWeighting} disabled={loading}>Apply Weighting</button>
          )}
          {Object.keys(weightingStats).length > 0 && (
            <button
              onClick={() => { setShowLtvPanel((p) => !p); setLtvResults([]); }}
              style={{ backgroundColor: "#7c3aed", color: "#fff", border: "none",
                       padding: "6px 14px", borderRadius: "4px", cursor: "pointer" }}
            >
              LTV Weighting
            </button>
          )}

          {showLtvPanel && (
            <div style={{
              margin: "12px 0", padding: "16px", border: "2px solid #7c3aed",
              borderRadius: "6px", backgroundColor: "#f5f3ff"
            }}>
              <h3 style={{ marginTop: 0, color: "#5b21b6" }}>LTV Weighting</h3>
              <p style={{ fontSize: "13px", color: "#555", margin: "0 0 10px" }}>
                Using weight: <strong>{weightingStats.weightingName}</strong>.
                Select variables with a 0–10 or 1–11 scale. Other scales are skipped.
              </p>
              <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "13px", fontWeight: 600 }}>
                    Select LTV Variables (hold Ctrl/Cmd for multiple):
                  </label>
                  <select
                    multiple
                    size={Math.min(8, Object.keys(dataGroup).length || 4)}
                    value={ltvSelectedKeys}
                    onChange={(e) =>
                      setLtvSelectedKeys(Array.from(e.target.selectedOptions, (o) => o.value))
                    }
                    style={{ width: "100%", marginTop: "6px", padding: "4px",
                             border: "1px solid #9f7aea", borderRadius: "4px", fontSize: "13px" }}
                  >
                    {Object.keys(dataGroup).map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>
                <div style={{ paddingTop: "22px" }}>
                  <button
                    onClick={applyLtvWeighting}
                    disabled={ltvLoading || !ltvSelectedKeys.length}
                    style={{
                      backgroundColor: ltvLoading || !ltvSelectedKeys.length ? "#ccc" : "#7c3aed",
                      color: "#fff", border: "none", padding: "8px 18px",
                      borderRadius: "4px", cursor: ltvLoading || !ltvSelectedKeys.length ? "not-allowed" : "pointer",
                      fontWeight: 600
                    }}
                  >
                    {ltvLoading ? "Computing..." : "Apply LTV Weight"}
                  </button>
                </div>
              </div>

              {ltvResults.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <h4 style={{ color: "#5b21b6", marginBottom: "8px" }}>Results</h4>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#ede9fe" }}>
                        <th style={thStyle}>Variable</th>
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Scale</th>
                        <th style={thStyle}>N (nonzero)</th>
                        <th style={thStyle}>Norm Factor</th>
                        <th style={thStyle}>Output Variable / Reason</th>
                        <th style={thStyle}>Out-of-range → 0</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ltvResults.map((r, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#faf5ff" : "#fff" }}>
                          <td style={tdStyle}>{r.variable}</td>
                          <td style={{ ...tdStyle, color: r.status === "success" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                            {r.status}{r.source ? ` (${r.source})` : ""}
                          </td>
                          <td style={tdStyle}>{r.scale || "—"}</td>
                          <td style={tdStyle}>{r.n_nonzero != null ? r.n_nonzero : "—"}</td>
                          <td style={tdStyle}>{r.normalization_factor != null ? r.normalization_factor : "—"}</td>
                          <td style={tdStyle}>{r.ltv_weight_var || r.reason || "—"}</td>
                          <td style={{ ...tdStyle, fontSize: "11px", color: "#92400e" }}>
                            {r.out_of_range_recoded_to_0?.length
                              ? r.out_of_range_recoded_to_0.join(", ")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {Object.keys(weightingData).length > 0 && (
            <>
              <h2>Selected Variables Frequency Data</h2>
              <table>
                <thead>
                  <tr>
                    <th>Variable Value</th>
                    <th>Frequency</th>
                    <th>Percentage</th>
                    <th>Target Percentage</th>
                    <th>Adjusted Target Percentage</th>
                    <th>Weighted Count</th>
                    <th>Weighted Percentage</th>
                    <th>Remove Variable</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(weightingData).map(([variable, keys]) => {
                    const totalFreq = getTotalFreq(variable);
                    const totalWeightedCount = getTotalWeightedCount(variable);
                    const adjustedTargets = calculateAdjustedTargets(variable);
                    let previousVariable = null;

                    return sortKeys(Object.entries(keys)).map(([key, row], index) => {
                      const freqNum = row.freq || 0;
                      const percentage = totalFreq > 0 ? (freqNum / totalFreq) * 100 : 0;
                      const adjustedTarget = (adjustedTargets[key] || 0) * 100;
                      const weightedCountNum = row.weightedCount != null ? row.weightedCount : freqNum;
                      const weightedPercentage = totalWeightedCount > 0
                        ? (weightedCountNum / totalWeightedCount) * 100
                        : 0;
                      const rowClass = previousVariable !== variable ? "dark-border" : "";
                      previousVariable = variable;
                      const label = getLabel(variable, key);

                      return (
                        <tr key={`${variable}-${key}-${index}`} className={rowClass}>
                          <td>
                            <span style={{ fontWeight: 600 }}>{variable}</span>{" "}
                            <span style={{ color: "#555" }}>
                              ({key}{label ? ` · ${label}` : ""})
                            </span>
                          </td>
                          <td>{freqNum.toFixed(2)}</td>
                          <td>{percentage.toFixed(2)}%</td>
                          <td>
                            <input
                              type="text"
                              value={row.target ?? ""}
                              onChange={(e) => handleTargetChange(variable, key, e.target.value)}
                              onPaste={(e) => handlePaste(e, variable, key)}
                            />
                          </td>
                          <td>{adjustedTarget.toFixed(2)}%</td>
                          <td>{weightedCountNum.toFixed(2)}</td>
                          <td>{weightedPercentage.toFixed(2)}%</td>
                          <td>
                            <button
                              onClick={() => removeVariable(variable)}
                              style={{ background: "transparent", border: "none", color: "red", cursor: "pointer" }}
                            >
                              &#x2715;
                            </button>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
{/* {JSON.stringify(weightingData)}
{JSON.stringify(currentSelectedVariables)} */}
    </div>
  );
}

export default Weighting;
